import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Edit, Trash2, AlertCircle, KeyRound } from 'lucide-react';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { useVendedorDetails } from '@/hooks/useVendedores';
import { EditarVendedor } from '../EditarVendedor';
import { VendedorOverviewTab } from './VendedorOverviewTab';
import { VendedorParceirosTab } from './VendedorParceirosTab';
import { VendedorPerformanceTab } from './VendedorPerformanceTab';
import { VendedorDocumentosTab } from './VendedorDocumentosTab';
import { VendedorFinanceiroTab } from './VendedorFinanceiroTab';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { toast } from 'sonner';
import { usePermissions } from '@/hooks/usePermissions';
import { useIsMobile } from '@/hooks/use-mobile';
import { VendedoresService } from '@/services/vendedoresService';

interface VendedorDetailsProps {
  vendedorId: string | null;
  onClose: () => void;
  onUpdate?: () => void;
}

export function VendedorDetails({ vendedorId, onClose, onUpdate }: VendedorDetailsProps) {
  const { vendedor, loading, error, refetch } = useVendedorDetails(vendedorId);
  const permissions = usePermissions();
  const isMobile = useIsMobile();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [hasRelatedDialogOpen, setHasRelatedDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [parceirosCount, setParceirosCount] = useState(0);
  const [leadsCount, setLeadsCount] = useState(0);
  const [resetSenhaDialogOpen, setResetSenhaDialogOpen] = useState(false);
  const [isResettingSenha, setIsResettingSenha] = useState(false);

  const handleEditSuccess = () => {
    refetch();
    if (onUpdate) onUpdate();
  };

  const handleDeleteClick = async () => {
    if (!vendedorId) return;

    // Verificar se o vendedor possui parceiros ou leads
    try {
      const response = await fetchWithAuth(`/api/vendedores/${vendedorId}/check-related`);
      
      if (!response.ok) {
        throw new Error('Erro ao verificar dados do vendedor');
      }

      const data = await response.json();
      
      if (data.hasRelated) {
        setParceirosCount(data.parceirosCount);
        setLeadsCount(data.leadsCount);
        setHasRelatedDialogOpen(true);
      } else {
        setDeleteDialogOpen(true);
      }
    } catch (error) {
      console.error('Erro ao verificar dados relacionados:', error);
      toast.error('Erro ao verificar se o vendedor possui dados relacionados');
    }
  };

  const handleConfirmDelete = async () => {
    if (!vendedorId) return;
    
    setIsDeleting(true);
    
    try {
      const response = await fetchWithAuth(`/api/vendedores/${vendedorId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao excluir vendedor');
      }

      toast.success('Vendedor excluído com sucesso!');
      setDeleteDialogOpen(false);
      onClose();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Erro ao excluir vendedor:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao excluir vendedor');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResetSenha = async () => {
    if (!vendedorId) return;
    
    setIsResettingSenha(true);
    
    try {
      const result = await VendedoresService.resetVendedorSenha(vendedorId);
      
      toast.success('Senha resetada com sucesso!', {
        description: (
          <div className="mt-2 space-y-1">
            <p className="font-semibold">Credenciais de acesso:</p>
            <p className="text-sm">Email: {result.credenciais.email}</p>
            <p className="text-sm">Senha: {result.credenciais.senha}</p>
            <p className="text-xs text-muted-foreground mt-2">
              O vendedor precisará fazer login e definir uma nova senha.
            </p>
          </div>
        ),
        duration: 10000
      });
      
      setResetSenhaDialogOpen(false);
      refetch();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Erro ao resetar senha:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao resetar senha');
    } finally {
      setIsResettingSenha(false);
    }
  };

  if (!vendedorId) return null;

  return (
    <Dialog open={!!vendedorId} onOpenChange={onClose}>
      <DialogContent
        className={`
          max-w-6xl max-h-[90vh] overflow-hidden
          flex flex-col p-4 sm:p-6
          max-md:inset-0 max-md:left-0 max-md:top-0 max-md:translate-x-0 max-md:translate-y-0
          max-md:h-[100dvh] max-md:max-h-[100dvh] max-md:rounded-none max-md:w-full max-md:max-w-none
        `}
      >
        <DialogHeader className="flex-shrink-0 space-y-0 pb-2 sm:pb-4">
          <div className="flex flex-col gap-3 max-sm:gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2 min-w-0 pr-8">
              <DialogTitle className="text-base sm:text-xl md:text-2xl">
                {loading ? 'Carregando...' : vendedor?.vendedor_nome || 'Detalhes do Vendedor'}
              </DialogTitle>
              {vendedor && (
                <Badge variant={vendedor.status ? 'default' : 'secondary'} className="flex-shrink-0">
                  {vendedor.status ? 'Ativo' : 'Inativo'}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {vendedor && !loading && permissions.userRole === 'admin' && (
                <>
                  <Button
                    variant="outline"
                    size={isMobile ? 'icon' : 'sm'}
                    onClick={() => setEditDialogOpen(true)}
                    title="Editar"
                    className="flex-shrink-0"
                  >
                    <Edit className="h-4 w-4 sm:mr-2" />
                    {!isMobile && 'Editar'}
                  </Button>
                  <Button
                    variant="outline"
                    size={isMobile ? 'icon' : 'sm'}
                    onClick={() => setResetSenhaDialogOpen(true)}
                    title="Resetar Senha"
                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 flex-shrink-0"
                  >
                    <KeyRound className="h-4 w-4 sm:mr-2" />
                    {!isMobile && 'Resetar Senha'}
                  </Button>
                  <Button
                    variant="outline"
                    size={isMobile ? 'icon' : 'sm'}
                    onClick={handleDeleteClick}
                    title="Excluir Vendedor"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                  >
                    <Trash2 className="h-4 w-4 sm:mr-2" />
                    {!isMobile && 'Excluir Vendedor'}
                  </Button>
                </>
              )}
            </div>
          </div>
          <DialogDescription className="max-md:sr-only">
            Visualize informações detalhadas, parceiros e métricas de performance do vendedor
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 flex-shrink-0">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12 flex-shrink-0">
            <div className="text-center">
              <p className="text-red-600 font-semibold mb-2">Erro ao carregar detalhes</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          </div>
        ) : vendedor ? (
          <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="w-full mb-16 md:mb-6 grid gap-1 p-1 grid-cols-2 md:grid-cols-5">
              <TabsTrigger value="overview">Visão Geral</TabsTrigger>
              <TabsTrigger value="parceiros">
                Parceiros ({vendedor.parceiros.length})
              </TabsTrigger>
              <TabsTrigger value="performance">Performance</TabsTrigger>
              <TabsTrigger value="documentos">Documentos</TabsTrigger>
              <TabsTrigger value="financeiro">Financeiro</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-16 md:mt-4">
              <VendedorOverviewTab vendedor={vendedor} />
            </TabsContent>

            <TabsContent value="parceiros" className="mt-16 md:mt-4">
              <VendedorParceirosTab vendedor={vendedor} />
            </TabsContent>

            <TabsContent value="performance" className="mt-16 md:mt-4">
              <VendedorPerformanceTab vendedor={vendedor} />
            </TabsContent>

            <TabsContent value="documentos" className="mt-16 md:mt-4">
              <VendedorDocumentosTab vendedorId={vendedor.vendedor_id} />
            </TabsContent>

            <TabsContent value="financeiro" className="mt-16 md:mt-4">
              <VendedorFinanceiroTab vendedorId={vendedor.vendedor_id} />
            </TabsContent>
          </Tabs>
          </div>
        ) : null}

        {/* Modal de Edição */}
        <EditarVendedor
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSuccess={handleEditSuccess}
          vendedor={vendedor || null}
        />
      </DialogContent>

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              Confirmar Exclusão do Vendedor
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-muted-foreground">
                Tem certeza que deseja excluir o vendedor <strong>{vendedor?.vendedor_nome}</strong>?
                <br /><br />
                Esta ação é <strong>irreversível</strong> e irá remover:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>O registro do vendedor</li>
                  <li>Todos os documentos associados</li>
                  <li>Todo o histórico de performance</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                'Excluir Vendedor'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de confirmação para resetar senha */}
      <AlertDialog open={resetSenhaDialogOpen} onOpenChange={setResetSenhaDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-orange-600" />
              Resetar Senha do Vendedor
            </AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja resetar a senha do vendedor <strong>{vendedor?.vendedor_nome}</strong>?
              <br /><br />
              A senha será resetada para a senha provisória padrão: <strong>Sun7energy</strong>
              <br /><br />
              <span className="text-sm text-muted-foreground">
                O vendedor precisará fazer login com a senha provisória e será redirecionado para definir uma nova senha definitiva.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResettingSenha}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetSenha}
              disabled={isResettingSenha}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {isResettingSenha ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Resetando...
                </>
              ) : (
                'Resetar Senha'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog quando vendedor possui parceiros ou leads */}
      <AlertDialog open={hasRelatedDialogOpen} onOpenChange={setHasRelatedDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              Não é Possível Excluir o Vendedor
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-muted-foreground">
                O vendedor <strong>{vendedor?.vendedor_nome}</strong> possui dados relacionados que impedem sua exclusão:
                <br /><br />
                {parceirosCount > 0 && (
                  <div className="mb-2">
                    • <strong>{parceirosCount}</strong> parceiro(s) vinculado(s)
                  </div>
                )}
                {leadsCount > 0 && (
                  <div className="mb-2">
                    • <strong>{leadsCount}</strong> lead(s) diretamente relacionado(s)
                  </div>
                )}
                <br />
                Para excluir este vendedor, você precisa primeiro:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  {parceirosCount > 0 && (
                    <li>Transferir todos os parceiros para outro vendedor</li>
                  )}
                  {leadsCount > 0 && (
                    <li>Transferir todos os leads para outro vendedor</li>
                  )}
                </ul>
                <br />
                Após realizar essas ações, você poderá excluir o vendedor.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Entendi</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

